import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, X, Loader2, Target, LayoutGrid, List, Search,
  GripVertical, IndianRupee, Trash2,
} from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, useDraggable, DragOverlay,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { SearchableSelect } from '@/components/shared/SearchableSelect';

interface Pipeline {
  id: string;
  name: string;
  stages: Stage[];
}

interface Stage {
  id: string;
  name: string;
  order: number;
  probability?: number;
  color?: string;
}

interface Deal {
  id: string;
  title: string;
  value: number;
  probability?: number;
  stageId: string;
  createdAt: string;
  contact?: { id: string; firstName: string; lastName: string } | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  stage?: Stage | null;
}

interface Account {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
}

type ViewMode = 'kanban' | 'list';

const STAGE_COLORS: Record<string, string> = {
  default: 'bg-gray-100 border-gray-300',
  blue: 'bg-blue-50 border-blue-300',
  yellow: 'bg-yellow-50 border-yellow-300',
  green: 'bg-green-50 border-green-300',
  red: 'bg-red-50 border-red-300',
  purple: 'bg-purple-50 border-purple-300',
};

/* ── @dnd-kit helper components ── */

function DroppableStageColumn({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex w-72 flex-shrink-0 flex-col rounded-xl border border-gray-200 bg-gray-50">
      {children}
    </div>
  );
}

function DroppableStageArea({ stageId, children }: { stageId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: stageId });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 space-y-2 p-3 overflow-y-auto max-h-[calc(100vh-320px)] transition-colors ${isOver ? 'bg-[var(--saffron-50)] ring-2 ring-[var(--ink-3)] ring-inset rounded-b-xl' : ''}`}
    >
      {children}
    </div>
  );
}

// Sprint Apr-30: drag neutralized — setNodeRef still required by @dnd-kit
// but listeners/attributes are detached so no real drag interaction occurs.
function DraggableDealCard({
  deal,
  isActive,
  onNavigate,
  onDelete,
  probabilityColor,
}: {
  deal: Deal;
  isActive: boolean;
  onNavigate: () => void;
  onDelete: () => void;
  probabilityColor: (prob?: number) => string;
}) {
  const { setNodeRef } = useDraggable({ id: deal.id });
  return (
    <div
      ref={setNodeRef}
      onClick={onNavigate}
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${isActive ? 'opacity-40' : ''}`}
    >
      <div className="mb-2 flex items-start justify-between">
        <h4 className="text-sm font-medium text-gray-900 leading-tight">{deal.title}</h4>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={12} /></button>
          {/* GripVertical retained visually but no drag listeners attached */}
          <span title="Stage change via call outcomes only"><GripVertical size={14} className="flex-shrink-0 text-gray-200" /></span>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="flex items-center gap-0.5 font-semibold text-gray-900">
          <IndianRupee size={12} />
          {formatINR(deal.value).replace('₹', '')}
        </span>
      </div>
      {deal.contact && (
        <p className="mt-1.5 text-xs text-gray-500">
          {deal.contact.firstName} {deal.contact.lastName}
        </p>
      )}
      {deal.probability != null && (
        <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${probabilityColor(deal.probability)}`}>
          {deal.probability}% probability
        </span>
      )}
    </div>
  );
}

export function DealsPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<string>('');
  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Add Deal Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', value: '', stageId: '', contactId: '', accountId: '', assignedToId: '', expectedCloseDate: '',
  });

  // Fetch pipelines
  useEffect(() => {
    api.get('/deals/pipelines')
      .then(res => {
        const data = res.data.data?.pipelines ?? res.data.data ?? [];
        setPipelines(data);
        if (data.length > 0 && !activePipelineId) {
          setActivePipelineId(data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch contacts and team members (for the modal)
  useEffect(() => {
    api.get('/contacts').then(res => {
      setContacts(res.data.data?.contacts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/accounts').then(res => {
      setAccounts(res.data.data?.accounts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/users').then(res => {
      setTeamMembers(res.data.data?.users ?? res.data.data ?? []);
    }).catch(() => {});
  }, []);

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (viewMode === 'list') {
        params.page = String(page);
        params.limit = String(limit);
      }
      if (activePipelineId) params.pipelineId = activePipelineId;
      if (search) params.search = search;
      const res = await api.get('/deals', { params });
      setDeals(res.data.data?.deals ?? res.data.data ?? []);
      if (viewMode === 'list') {
        setTotal(res.data.data?.pagination?.total ?? res.data.pagination?.total ?? res.data.total ?? 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [activePipelineId, search, page, viewMode]);

  useEffect(() => {
    if (activePipelineId) fetchDeals();
  }, [fetchDeals, activePipelineId]);

  const activePipeline = pipelines.find(p => p.id === activePipelineId);
  const stages = activePipeline?.stages?.sort((a, b) => a.order - b.order) ?? [];

  const dealsByStage = (stageId: string) => deals.filter(d => d.stageId === stageId);

  const stageTotal = (stageId: string) =>
    // Coerce with Number() — Prisma Decimal columns deserialize as strings over
    // JSON, so `sum + "4500000"` becomes string concatenation and produces huge
    // exponent values (Ishan F-11 — ₹4.5000e+107 on the Deal Kanban).
    dealsByStage(stageId).reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/deals', {
        title: form.title,
        value: parseFloat(form.value) || 0,
        stageId: form.stageId,
        contactId: form.contactId || undefined,
        accountId: form.accountId || undefined,
        assignedToId: form.assignedToId || undefined,
        expectedClose: form.expectedCloseDate || undefined,
      });
      setShowModal(false);
      setForm({ title: '', value: '', stageId: '', contactId: '', accountId: '', assignedToId: '', expectedCloseDate: '' });
      fetchDeals();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create deal');
    } finally {
      setSaving(false);
    }
  };

  // ── @dnd-kit drag-and-drop ───────────────────────────────────
  const [activeDealId, setActiveDealId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Sprint Apr-30: drag-to-change-stage neutralized.
  // Deal stage advances only through call dispositions / proposal events.
  const [dragToastVisible, setDragToastVisible] = useState(false);

  const handleDndDragStart = (_event: DragStartEvent) => {
    setActiveDealId(null); // don't actually drag
    setDragToastVisible(true);
    setTimeout(() => setDragToastVisible(false), 3000);
  };

  const handleDndDragEnd = (_event: DragEndEvent) => {
    setActiveDealId(null);
    // No-op: stage change via drag is disabled.
  };

  const activeDeal = activeDealId ? deals.find(d => d.id === activeDealId) : null;

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this deal?')) return;
    try {
      await api.delete(`/deals/${id}`);
      fetchDeals();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete deal');
    }
  };

  const probabilityColor = (prob?: number) => {
    if (prob == null) return 'bg-gray-100 text-gray-600';
    if (prob >= 75) return 'bg-green-100 text-green-700';
    if (prob >= 50) return 'bg-yellow-100 text-yellow-700';
    if (prob >= 25) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Sprint Apr-30: no-drag toast */}
      {dragToastVisible && (
        <div style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--ink-0, #1B1713)', color: 'var(--ivory-1, #FAF7EF)',
          padding: '10px 18px', borderRadius: '10px', fontSize: '13px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 9999, pointerEvents: 'none',
          maxWidth: '380px', textAlign: 'center',
        }}>
          Stages advance via call outcomes / proposal events. Click the lead to log a call disposition.
        </div>
      )}

      {/* Header — Apr 23, 2026 (Agent 4 / Fix B): grid with auto-column on
          ≥sm breakpoint so Add Deal + pipeline selector + view toggle are
          always pinned right, even at 1512px with AI panel open. Below sm
          (mobile) we fall back to a stacked layout. */}
      <div className="mb-6 flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-gray-900">Deals</h1>
          <p className="truncate text-sm text-gray-500">Manage your sales pipeline</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
          {/* Pipeline Selector — always shown so Kunwar can confirm the active
              pipeline (Bug 15: "No option to choose pipeline as sun stellar sales").
              Previously gated on pipelines.length > 1 which hid it when a tenant
              had only one pipeline. */}
          {pipelines.length > 0 && (
            <select
              value={activePipelineId}
              onChange={(e) => setActivePipelineId(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              title="Active pipeline"
            >
              {pipelines.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {/* View Toggle */}
          <div className="flex rounded-lg border border-gray-300 bg-white p-0.5">
            <button
              onClick={() => setViewMode('kanban')}
              className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
              style={
                viewMode === 'kanban'
                  ? { background: 'var(--ink-0)', color: 'var(--ivory-0)' }
                  : { background: 'transparent', color: 'var(--ink-3)' }
              }
              title="Kanban view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
              style={
                viewMode === 'list'
                  ? { background: 'var(--ink-0)', color: 'var(--ivory-0)' }
                  : { background: 'transparent', color: 'var(--ink-3)' }
              }
              title="List view"
            >
              <List size={16} />
            </button>
          </div>
          {/* Apr 27, 2026 (Kunwar bug) — Add Deal button was invisible because
              Tailwind v4 dropped the `primary-*` palette → bg-[var(--ink-0)] didn't
              render → white text on transparent bg. Same fix as LoginPage:
              direct CSS-var inline styles. Cannot regress. */}
          <button
            onClick={() => {
              if (stages.length > 0) setForm({ ...form, stageId: stages[0].id });
              setShowModal(true);
            }}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            style={{
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
              border: '1px solid var(--ink-0)',
            }}
            aria-label="Add new deal"
          >
            <Plus size={16} />
            Add Deal
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search deals..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading deals...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchDeals} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        <DndContext sensors={sensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {stages.length === 0 ? (
              <div className="flex flex-col items-center justify-center w-full py-20 text-gray-400">
                <Target size={40} className="mb-2" />
                <p className="text-sm">No pipeline stages configured</p>
                <p className="text-xs">Set up your pipeline in Settings</p>
              </div>
            ) : (
              stages.map(stage => (
                <DroppableStageColumn key={stage.id}>
                  {/* Stage Header */}
                  <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 rounded-t-xl">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{stage.name}</h3>
                      <p className="text-xs text-gray-500">
                        {dealsByStage(stage.id).length} deals &middot; {formatINR(stageTotal(stage.id))}
                      </p>
                    </div>
                    {stage.probability != null && (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${probabilityColor(stage.probability)}`}>
                        {stage.probability}%
                      </span>
                    )}
                  </div>

                  {/* Deal Cards */}
                  <DroppableStageArea stageId={stage.id}>
                    {dealsByStage(stage.id).length === 0 ? (
                      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-8 text-gray-400">
                        <p className="text-xs">No deals</p>
                      </div>
                    ) : (
                      dealsByStage(stage.id).map(deal => (
                        <DraggableDealCard
                          key={deal.id}
                          deal={deal}
                          isActive={activeDealId === deal.id}
                          onNavigate={() => navigate(`/deals/${deal.id}`)}
                          onDelete={() => handleDelete(deal.id)}
                          probabilityColor={probabilityColor}
                        />
                      ))
                    )}
                  </DroppableStageArea>
                </DroppableStageColumn>
              ))
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeDeal ? (
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg w-72 opacity-90 rotate-2">
                <div className="mb-2 flex items-start justify-between">
                  <h4 className="text-sm font-medium text-gray-900 leading-tight">{activeDeal.title}</h4>
                  <GripVertical size={14} className="flex-shrink-0 text-gray-300" />
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-0.5 font-semibold text-gray-900">
                    <IndianRupee size={12} />
                    {formatINR(activeDeal.value).replace('₹', '')}
                  </span>
                </div>
                {activeDeal.contact && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    {activeDeal.contact.firstName} {activeDeal.contact.lastName}
                  </p>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        /* List View */
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {deals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Target size={40} className="mb-2" />
              <p className="text-sm">No deals found</p>
              <p className="text-xs">Create your first deal to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Value</th>
                    <th className="px-4 py-3">Stage</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Probability</th>
                    <th className="px-4 py-3">Assigned To</th>
                    <th className="px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deals.map(deal => {
                    const stageName = deal.stage?.name || stages.find(s => s.id === deal.stageId)?.name || '-';
                    return (
                      <tr key={deal.id} onClick={() => navigate(`/deals/${deal.id}`)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-4 py-3 font-medium text-gray-900">{deal.title}</td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{formatINR(deal.value)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {stageName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {deal.contact ? `${deal.contact.firstName} ${deal.contact.lastName}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {deal.probability != null ? (
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${probabilityColor(deal.probability)}`}>
                              {deal.probability}%
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {deal.assignedTo ? `${deal.assignedTo.firstName} ${deal.assignedTo.lastName}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(deal.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination (list view) */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Deal Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Deal</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Value (INR) *</label>
                  <input
                    type="number"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    placeholder="0"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Stage *</label>
                  <select
                    value={form.stageId}
                    onChange={(e) => setForm({ ...form, stageId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select Stage</option>
                    {stages.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact</label>
                  <SearchableSelect
                    value={form.contactId || null}
                    onChange={(v) => setForm({ ...form, contactId: v ?? '' })}
                    options={contacts.map((c) => ({
                      value: c.id,
                      label: `${c.firstName} ${c.lastName}`.trim(),
                    }))}
                    placeholder="None"
                    searchPlaceholder="Search contacts…"
                    emptyMessage="No matching contacts"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                  <SearchableSelect
                    value={form.accountId || null}
                    onChange={(v) => setForm({ ...form, accountId: v ?? '' })}
                    options={accounts.map((a) => ({ value: a.id, label: a.name }))}
                    placeholder="None"
                    searchPlaceholder="Search accounts…"
                    emptyMessage="No matching accounts"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Assigned To</label>
                  <select
                    value={form.assignedToId}
                    onChange={(e) => setForm({ ...form, assignedToId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Expected Close Date</label>
                  <input
                    type="date"
                    value={form.expectedCloseDate}
                    onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.title || !form.value || !form.stageId}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
                style={{
                  background: 'var(--ink-0)',
                  color: 'var(--ivory-0)',
                  border: '1px solid var(--ink-0)',
                }}
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Deal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
